/**
 * GlobalSearch - Full-text search component for tasks and assets
 *
 * Features:
 * - Debounced search input
 * - Keyboard shortcut (Cmd/Ctrl + K)
 * - Click-through navigation to results
 * - Color-coded results by entity type
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Popper,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Chip,
  CircularProgress,
  IconButton,
  ClickAwayListener,
  Fade,
  Tooltip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Search as SearchIcon,
  Assignment as TaskIcon,
  Inventory as AssetIcon,
  Close as CloseIcon,
  Keyboard as KeyboardIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../apiConfig';
import { TRAK } from '../theme/trakTokens';
import { trackEvent } from '../services/analyticsService';
import { EVENTS, SURFACE } from '../services/analyticsEvents';
import {
  exactTaskIdMatches, taskIdHistoryHref, taskIdToLookUp, sameIdSummary,
} from '../utils/taskIdHistory';
import { countTasksWithId } from '../hooks/useTaskIdCount';

// Debounce hook
const useDebounce = (callback, delay) => {
  const timeoutRef = useRef(null);

  const debouncedCallback = useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => callback(...args), delay);
  }, [callback, delay]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return debouncedCallback;
};

function GlobalSearch() {
  const navigate = useNavigate();
  const { getIdToken } = useAuth();
  const subscriberId = localStorage.getItem('subscriberId');

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const [totalFound, setTotalFound] = useState(0);

  const anchorRef = useRef(null);
  const inputRef = useRef(null);

  // On a phone the field's 200px floor is a third of the bar, and it sits next
  // to a Ctrl+K hint no phone can act on. Below md it collapses to the same
  // 30px round button as the rest of the right-hand cluster and expands over
  // the bar when tapped, so the workspace name and the nav row get the width.
  // A tablet keeps the field: between sm and md the bar wraps, and the field
  // grows into whatever line 1 has left rather than declaring a width of its
  // own (see flexBasis below) — under `flex-wrap` an item with a real basis
  // takes a new line rather than shrinking, and a 300px basis at 700px pushed
  // the whole right-hand cluster onto a third row.
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('sm'));
  // The Ctrl+K hint is 55px of keyboard advice, and below md the field is down
  // to its 140px floor — on the devices that reach this width there is usually
  // no Ctrl to press.
  const showShortcutHint = !useMediaQuery(theme.breakpoints.down('md'));
  const [expanded, setExpanded] = useState(false);
  const collapsed = isCompact && !expanded;

  // The tasks that carry exactly the typed ID, counted in Firestore and not in
  // the search index: `{ value, atLeast, exact }`, or null while unknown.
  const [idMatch, setIdMatch] = useState(null);
  const idLookupRef = useRef(0);

  // Asked after every search, whatever the search answered, so an ID is still
  // found when the index is behind or the search service is down. Never
  // awaited by the search and never an error: a refused count (a customer
  // session) leaves the row to the search hits alone.
  const lookUpId = useCallback((searchQuery, hits) => {
    const lookup = idLookupRef.current + 1;
    idLookupRef.current = lookup;
    setIdMatch(null);
    const value = taskIdToLookUp(hits, searchQuery);
    if (!subscriberId || value.length < 3) return;
    countTasksWithId(subscriberId, value)
      .then((counted) => {
        if (idLookupRef.current !== lookup || counted.atLeast < 1) return;
        setIdMatch({ value, ...counted });
        setOpen(true);
      })
      .catch(() => {});
  }, [subscriberId]);

  // Perform search
  const performSearch = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      idLookupRef.current += 1;
      setIdMatch(null);
      setResults([]);
      setTotalFound(0);
      setError(null);
      // Nothing to say below the 2-character floor, and the panel now opens on
      // every other outcome — leaving it open here would hold an empty white
      // box under the field while the query is being deleted.
      setOpen(false);
      return;
    }

    if (!subscriberId) {
      setError('No subscriber selected');
      setOpen(true);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = await getIdToken();
      const params = new URLSearchParams({
        q: searchQuery,
        subscriber_id: subscriberId,
        limit: '20'
      });

      const response = await fetch(
        `${API_BASE_URL}/api/v1/search/?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Open the panel for every outcome, not just a non-empty hit list. The
      // panel is the only place this component can say anything — it renders
      // the error text and the "No results found" line — and opening it solely
      // on `results.length > 0` made both unreachable. A 403 from the search
      // endpoint then looked exactly like a working search over an empty
      // index: type, nothing happens, no way to tell which.
      if (response.ok) {
        const data = await response.json();
        const hits = data.results || [];
        setResults(hits);
        setTotalFound(data.total_found || 0);
        setOpen(true);
        lookUpId(searchQuery, hits);
        // How many hits came back, never what was typed to get them. A search
        // query is free text a user can put anything into — a customer's name,
        // a container number — and GA4 keeps what it is given.
        //
        // One user intent can emit several of these: the field searches from
        // two characters on a 300ms debounce, so a container number typed with
        // a pause in the middle is two searches served. The event counts round
        // trips, not distinct queries — see the Task 6 runbook.
        trackEvent(EVENTS.SEARCH_PERFORMED, {
          surface: SURFACE.GLOBAL_SEARCH,
          trigger: 'query',
          result_count: hits.length,
        });
      } else if (response.status === 403) {
        const data = await response.json();
        setError(data.detail || 'Search not available');
        setResults([]);
        setOpen(true);
        lookUpId(searchQuery, []);
      } else if (response.status === 503) {
        setError('Search service unavailable');
        setResults([]);
        setOpen(true);
        lookUpId(searchQuery, []);
      } else {
        setError('Search failed');
        setResults([]);
        setOpen(true);
        lookUpId(searchQuery, []);
      }
    } catch (err) {
      console.error('Search error:', err);
      setError('Connection error');
      setResults([]);
      setOpen(true);
      lookUpId(searchQuery, []);
    } finally {
      setLoading(false);
    }
  }, [subscriberId, getIdToken, lookUpId]);

  const debouncedSearch = useDebounce(performSearch, 300);

  // Tasks carry exactly the ID that was typed: offer the ID's own page first.
  // The count from Firestore decides (one task is enough, since the search
  // hits below may be near misses that do not include it); without it, two
  // exact search hits do, as before. See exactTaskIdMatches.
  const sameIdCount = exactTaskIdMatches(results, query);
  const showSameId = Boolean(idMatch) || sameIdCount > 1;
  const sameIdValue = idMatch ? idMatch.value : query.trim();

  const handleSameIdClick = () => {
    const href = taskIdHistoryHref(sameIdValue);
    setOpen(false);
    setQuery('');
    setResults([]);
    idLookupRef.current += 1;
    setIdMatch(null);
    navigate(href);
  };

  const sameIdRow = showSameId ? (
    <ListItem
      onClick={handleSameIdClick}
      sx={{
        cursor: 'pointer',
        backgroundColor: 'action.selected',
        '&:hover': { backgroundColor: 'action.hover' },
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <ListItemIcon sx={{ minWidth: 40 }}>
        <HistoryIcon fontSize="small" color="primary" />
      </ListItemIcon>
      <ListItemText
        primary={
          <Typography variant="body2" noWrap>
            All tasks with ID "{sameIdValue}"
          </Typography>
        }
        secondary={
          <Typography variant="caption" color="text.secondary" component="span">
            {sameIdSummary(idMatch ? idMatch.exact : null)}
          </Typography>
        }
      />
    </ListItem>
  ) : null;

  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    debouncedSearch(value);
  };

  // Handle result click
  const handleResultClick = (result) => {
    setOpen(false);
    setQuery('');
    setResults([]);
    idLookupRef.current += 1;
    setIdMatch(null);

    if (result.entity_type === 'task') {
      navigate(`/tasks/${result.id}`);
    } else if (result.entity_type === 'asset') {
      navigate(`/assets/${result.id}`);
    }
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Cmd/Ctrl + K to focus search
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setExpanded(true);
        inputRef.current?.focus();
      }

      // Escape closes the results, and on a phone puts the bar back together.
      if (e.key === 'Escape' && (open || expanded)) {
        setOpen(false);
        setExpanded(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, expanded]);

  // The field only exists from the tap that expanded it, so focus has to wait
  // for the render that mounts it.
  useEffect(() => {
    if (expanded) inputRef.current?.focus();
  }, [expanded]);

  // Handle focus
  const handleFocus = () => {
    if (results.length > 0) {
      setOpen(true);
    }
  };

  // Handle click away
  const handleClickAway = () => {
    setOpen(false);
    // Keep it open while there is something typed in it — collapsing would
    // throw the query away on any stray tap.
    if (!query) setExpanded(false);
  };

  // Clear search
  const handleClear = () => {
    setQuery('');
    setResults([]);
    idLookupRef.current += 1;
    setIdMatch(null);
    setOpen(false);
    setError(null);
    inputRef.current?.focus();
  };

  // Get icon for entity type
  const getIcon = (entityType) => {
    switch (entityType) {
      case 'task':
        return <TaskIcon sx={{ color: 'primary.main' }} />;
      case 'asset':
        return <AssetIcon sx={{ color: 'success.main' }} />;
      default:
        return <SearchIcon />;
    }
  };

  // Get chip color for entity type
  const getChipColor = (entityType) => {
    switch (entityType) {
      case 'task':
        return 'primary';
      case 'asset':
        return 'success';
      default:
        return 'default';
    }
  };

  if (collapsed) {
    return (
      <Tooltip title="Search">
        <IconButton
          onClick={() => setExpanded(true)}
          aria-label="Search"
          sx={{
            width: 30,
            height: 30,
            flexShrink: 0,
            borderRadius: '7px',
            bgcolor: 'rgba(255,255,255,.10)',
            color: 'rgba(255,255,255,.8)',
            '&:hover': { bgcolor: 'rgba(255,255,255,.26)' },
          }}
        >
          <SearchIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box
        sx={{
          // The search box is the one thing in the app bar genuinely happy to
          // be narrower, so it is the one that gives. Below md the bar wraps
          // and the nav row has its own line, so the field asks for nothing
          // and grows into what line 1 has left. From md it shares one row
          // with the destinations and goes back to a stated width — one it
          // keeps when there is room, a 140px floor it will not go below, and
          // ten times the nav row's willingness to shrink in between. As a
          // fixed width it held its 400px while the nav row beside it wrapped,
          // and the bar overflowed the viewport regardless.
          flexGrow: { xs: 1, md: 0 },
          flexShrink: 10,
          flexBasis: { xs: 0, md: 400 },
          minWidth: 140,
          // Expanded on a phone it lies over the bar rather than inside the
          // row: at that width there is no arrangement where the field and the
          // workspace name both fit, and the row would reflow on every tap.
          // The containing block is the sticky AppBar, which is outside the
          // toolbar's overflow clip. It carries the bar's own green, opaque —
          // the field's translucent fill alone left the workspace name legible
          // straight through the placeholder.
          ...(isCompact
            ? {
                position: 'absolute',
                left: 0,
                right: 0,
                top: 0,
                height: 54,
                display: 'flex',
                alignItems: 'center',
                px: { xs: 1.25, sm: 2.25 },
                bgcolor: TRAK.green,
                zIndex: 5,
              }
            : { position: 'relative' }),
        }}
      >
        <TextField
          ref={anchorRef}
          inputRef={inputRef}
          fullWidth
          size="small"
          placeholder="Search tasks, assets..."
          value={query}
          onChange={handleInputChange}
          onFocus={handleFocus}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              borderRadius: 2,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.25)',
              },
              '&.Mui-focused': {
                backgroundColor: 'rgba(255, 255, 255, 0.25)',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'transparent',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.3)',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
              },
            },
            '& .MuiInputBase-input': {
              color: 'white',
              '&::placeholder': {
                color: 'rgba(255, 255, 255, 0.7)',
                opacity: 1,
              },
            },
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  {loading ? (
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                  ) : (
                    <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                  )}
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {query ? (
                    <IconButton
                      size="small"
                      onClick={handleClear}
                      sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  ) : isCompact ? (
                    <IconButton
                      size="small"
                      onClick={() => setExpanded(false)}
                      aria-label="Close search"
                      sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  ) : !showShortcutHint ? null : (
                    <Chip
                      size="small"
                      icon={<KeyboardIcon sx={{ fontSize: 14 }} />}
                      label={navigator.platform.includes('Mac') ? 'K' : 'Ctrl+K'}
                      sx={{
                        height: 20,
                        fontSize: 11,
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        color: 'rgba(255, 255, 255, 0.7)',
                        '& .MuiChip-icon': {
                          color: 'rgba(255, 255, 255, 0.7)',
                        },
                      }}
                    />
                  )}
                </InputAdornment>
              ),
            }
          }}
        />

        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          transition
          style={{ zIndex: 1301 }}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={200}>
              <Paper
                elevation={8}
                sx={{
                  width: anchorRef.current?.offsetWidth || 400,
                  maxHeight: 400,
                  overflow: 'auto',
                  mt: 1,
                  borderRadius: 2,
                }}
              >
                {error ? (
                  <>
                    {sameIdRow && <List dense sx={{ py: 0, mb: 0 }}>{sameIdRow}</List>}
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        {error}
                      </Typography>
                    </Box>
                  </>
                ) : results.length === 0 && query.length >= 2 && !loading ? (
                  <>
                    {sameIdRow && <List dense sx={{ py: 0, mb: 0 }}>{sameIdRow}</List>}
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        {sameIdRow
                          ? `Search has nothing else for "${query}"`
                          : `No results found for "${query}"`}
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <>
                    {totalFound > results.length && (
                      <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
                        <Typography variant="caption" color="text.secondary">
                          Showing {results.length} of {totalFound} results
                        </Typography>
                      </Box>
                    )}
                    <List dense sx={{ py: 0, mb: 0 }}>
                      {sameIdRow}
                      {results.map((result) => (
                        <ListItem
                          key={`${result.entity_type}-${result.id}`}
                          onClick={() => handleResultClick(result)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: 'action.hover',
                            },
                            borderBottom: 1,
                            borderColor: 'divider',
                            '&:last-child': {
                              borderBottom: 0,
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            {getIcon(result.entity_type)}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                                  {result.title}
                                </Typography>
                                <Chip
                                  label={result.entity_type}
                                  size="small"
                                  color={getChipColor(result.entity_type)}
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: 11 }}
                                />
                              </Box>
                            }
                            secondary={
                              <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  noWrap
                                  component="span"
                                  sx={{ flex: 1 }}
                                >
                                  {result.subtitle || ''}
                                </Typography>
                                <Box component="span" sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 1, flexShrink: 0 }}>
                                  {result.image_count > 0 && (
                                    <Typography variant="caption" color="text.disabled" component="span" sx={{ fontSize: 10 }}>
                                      {result.image_count} img
                                    </Typography>
                                  )}
                                  {result.created_at && (
                                    <Typography variant="caption" color="text.disabled" component="span" sx={{ fontSize: 10 }}>
                                      {new Date(result.created_at * 1000).toLocaleDateString()}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                    {totalFound > 0 && (
                      <Box
                        sx={{
                          p: 1,
                          textAlign: 'center',
                          borderTop: 1,
                          borderColor: 'divider',
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'action.hover' },
                        }}
                        onClick={() => {
                          setOpen(false);
                          setQuery('');
                          setResults([]);
                          idLookupRef.current += 1;
                          setIdMatch(null);
                          navigate(`/search?q=${encodeURIComponent(query)}`);
                        }}
                      >
                        <Typography variant="caption" color="primary">
                          View all {totalFound} results in Advanced Search
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </Paper>
            </Fade>
          )}
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}

export default GlobalSearch;
